/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';
import { OpenAPIV3 } from 'openapi-types';

import { isWriterOfAssistant } from '@ibm-watson/shared/modules/ia/store/access-control/selectors';
import { selectIsNewIa } from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { getAssistantId } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { CatalogItem } from '@ibm-watson/shared/ts/catalog';
import { CreateAsyncThunk } from '@ibm-watson/shared/ts/state';
import {
  getVariableIdFromVariableValue,
  isStaticVariable
} from '../../../actions/util/variableUtil';
import { isCpd } from '../../../ia/util/config';
import * as api from '../../api/catalog';
import {
  getDefaultCatalogItems,
  CatalogItemDetails
} from '../../util/catalogItems';
import { UpdateCatalogItemResponse } from '../../util/types';
import { updateCatalogItemSpecificationBody } from './reducer';
interface importCatalogItemProps {
  assistantId: string;
  catalogItem: CatalogItem;
  updatedSpecBody: OpenAPIV3.Document<{}>;
}

/**
 * Updates the catalog OpenAPI specification body when the
 * spec is updated
 */
export const updateCatalogItem = createAsyncThunk<
  CatalogItem,
  CreateCatalogItemProps,
  CreateAsyncThunk
>(
  'catalog/updateCatalogItem',
  async (
    { assistantId, catalogItem, catalogItemDetails },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const safeAssistantId = assistantId ?? getAssistantId(getState());
      let safeCatalogItem = catalogItem;

      if (!_.isNil(catalogItemDetails)) {
        const { title, description, specification, id } = catalogItemDetails;

        safeCatalogItem = {
          title,
          description,
          catalog_item_id: id,
          specification
        };
      }
      const response = await api.updateCatalogIntegrationInstance(
        safeAssistantId,
        safeCatalogItem
      );
      return response;
    } catch (error) {
      if (
        error.response &&
        (error.response.status === 403 || error.response.status === 401)
      ) {
        dispatch(addErrorMessage(error));
      }
      return rejectWithValue(error);
    }
  }
);

/**
 * This function updates a single catalog item's specification body when a new
 * OpenAPI spec document is provided in the UpdateCatalogOpenApiSpecModal
 */
export const importCatalogItem = createAsyncThunk<
  void,
  importCatalogItemProps,
  CreateAsyncThunk
>(
  'catalog/importCatalogItem',
  async ({ assistantId, catalogItem, updatedSpecBody }, { dispatch }) => {
    try {
      dispatch(
        updateCatalogItemSpecificationBody({
          specification: { body: updatedSpecBody }
        })
      );
      const catalogItemDetails = {
        ...catalogItem,
        id: catalogItem.catalog_item_id,
        specification: { ...catalogItem.specification, body: updatedSpecBody }
      };
      const response: UpdateCatalogItemResponse = await dispatch(
        updateCatalogItem({
          assistantId,
          catalogItem,
          catalogItemDetails
        })
      );
      dispatch(
        updateCatalogItemSpecificationBody({
          specification: response?.payload?.specification
        })
      );
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

/**
 * This function grabs the newly updated OpenAPI spec's request parameters for
 * each operation in a structure that desired for the step resolver's callout
 * @param operation
 * @returns
 */
const getRequestMapping = operation => {
  const requestMappingObj = {
    header: [],
    path: [],
    body: [],
    query: []
  };
  const allParamsWithScalars = operation?.request_properties?.map(param => {
    if (!param?.assignedValue && isStaticVariable(param?.value)) {
      return {
        ...param,
        assignedValue: param.value
      };
    }
    return param;
  });
  const requiredParams =
    allParamsWithScalars?.filter(param => param.required) || [];
  const optionalParams =
    allParamsWithScalars?.filter(param => !param.required) || [];
  const operationParams = [...requiredParams, ...optionalParams];

  operationParams?.forEach(param => {
    const reqObj = {};

    _.set(reqObj, 'parameter', param.path);

    switch (param?.property_type) {
      case 'header':
        requestMappingObj.header.push(reqObj);
        break;
      case 'body':
        requestMappingObj.body.push(reqObj);
        break;
      case 'path':
        requestMappingObj.path.push(reqObj);
        break;
      case 'query':
        requestMappingObj.query.push(reqObj);
        break;
      default:
        return;
    }
  });
  // remove param sections without params
  Object.keys(requestMappingObj).forEach(paramSection => {
    if (!requestMappingObj[paramSection].length) {
      delete requestMappingObj[paramSection];
    }
  });
  // only add request_mapping to callout if requestMappingObject isn't empty
  if (Object.keys(requestMappingObj).length) {
    return requestMappingObj;
  } else {
    return null;
  }
};

/**
 * Gets the current step resolver's updated request params
 * @param stepResolver
 * @param operation
 * @param requestParams
 * @returns
 */
export const getUpdatedRequestMapping = (
  stepResolver,
  operation,
  requestParams
) => {
  const existingRequestMapping = _.get(
    stepResolver,
    'callout.request_mapping',
    null
  );
  const requestMappingResolverBody = existingRequestMapping?.body;
  const requestMappingResolverQuery = existingRequestMapping?.query;
  const requestMappingResolverPath = existingRequestMapping?.path;
  const requestMappingResolverHeader = existingRequestMapping?.header;
  // Uses the newly updated version of the operation in use within the step
  // to get the updated step resolver's request params
  const requestMapping = getRequestMapping(operation);
  const requestMappingBody = requestMapping?.body || [];
  const requestMappingQuery = requestMapping?.query || [];
  const requestMappingPath = requestMapping?.path || [];
  const requestMappingHeader = requestMapping?.header || [];
  // We must keep the previously set values of old request params
  const updatedRequestMapping: any = {};
  const newBody = [];
  requestMappingBody?.forEach(param => {
    const mutualParam = requestMappingResolverBody?.find(
      oldParam => oldParam.parameter === param.parameter
    );
    const operationParam = requestParams?.find(
      p => p.title === param.parameter
    );
    if (mutualParam) {
      param.value = mutualParam.value;
      newBody.push(param);
    } else if (operationParam?.required) {
      // In order to avoid a schema validation error when updating the action with the store API call below,
      // each param must have a non empty value. The workaround is to serialize the expression object to a
      // string and store it, or simply making the expression value '{}'. This is only needed for required params.
      param.value = {
        categoryId: 'expression',
        expression: '{}'
      };
      newBody.push(param);
    }
  });
  const newQuery = [];
  requestMappingQuery?.forEach(param => {
    const mutualParam = requestMappingResolverQuery?.find(
      oldParam => oldParam.parameter === param.parameter
    );
    const operationParam = requestParams?.find(
      p => p.title === param.parameter
    );
    if (mutualParam) {
      param.value = mutualParam.value;
      newQuery.push(param);
    } else if (operationParam?.required) {
      // In order to avoid a schema validation error when updating the action with the store API call below,
      // each param must have a non empty value. The workaround is to serialize the expression object to a
      // string and store it, or simply making the expression value '{}'. This is only needed for required params.
      param.value = {
        categoryId: 'expression',
        expression: '{}'
      };
      newQuery.push(param);
    }
  });
  requestMappingPath?.forEach(param => {
    const mutualParam = requestMappingResolverPath?.find(
      oldParam => oldParam.parameter === param.parameter
    );
    if (mutualParam) {
      param.value = mutualParam.value;
    } else {
      // In order to avoid a schema validation error when updating the action with the store API call below,
      // each param must have a non empty value. The workaround is to serialize the expression object to a
      // string and store it, or simply making the expression value '{}'.
      // This is only needed for required params, which is ALL path params;
      param.value = {
        categoryId: 'expression',
        expression: '{}'
      };
    }
  });
  const newHeader = [];
  requestMappingHeader?.forEach(param => {
    const mutualParam = requestMappingResolverHeader?.find(
      oldParam => oldParam.parameter === param.parameter
    );
    const operationParam = requestParams?.find(
      p => p.title === param.parameter
    );
    if (mutualParam) {
      param.value = mutualParam.value;
      newHeader.push(param);
    } else if (operationParam?.required) {
      // In order to avoid a schema validation error when updating the action with the store API call below,
      // each param must have a non empty value. The workaround is to serialize the expression object to a
      // string and store it, or simply making the expression value '{}'. This is only needed for required params.
      param.value = {
        categoryId: 'expression',
        expression: '{}'
      };
      newHeader.push(param);
    }
  });
  if (!_.isEmpty(newBody)) {
    updatedRequestMapping.body = newBody;
  }
  if (!_.isEmpty(newQuery)) {
    updatedRequestMapping.query = newQuery;
  }
  if (!_.isEmpty(requestMappingPath)) {
    updatedRequestMapping.path = requestMappingPath;
  }
  if (!_.isEmpty(newHeader)) {
    updatedRequestMapping.header = newHeader;
  }
  return updatedRequestMapping;
};

/**
 * Gets the current step resolver's parsed request params
 * so we can check if required params have been added
 * @param reqMap
 * @param operation
 * @param allVariables
 * @returns
 */
export const parseRequestMapping = (reqMap, operation, allVariables) => {
  let params = [];
  if (reqMap) {
    params = Object.keys(reqMap).reduce(
      (accu, currentProp) => accu.concat(reqMap[currentProp]),
      []
    );
    params = params.map(param => {
      const operationProperty = operation?.request_properties.find(
        prop => prop.path === param.parameter
      );
      let assignedValue;
      if (param?.value && Object.keys(param.value).includes('expression')) {
        assignedValue = { ...param.value, categoryId: 'expression' };
      } else {
        const key = getVariableIdFromVariableValue(param.value);
        if (key) {
          assignedValue = allVariables[key];
        }
      }
      return {
        assignedValue,
        path: param.parameter,
        ...param,
        ...operationProperty
      };
    });
  }
  return params;
};

interface CreateCatalogItemProps {
  assistantId?: string;
  catalogItem?: CatalogItem;
  catalogItemDetails?: CatalogItemDetails;
}
/**
 *
 *
 */
export const createCatalogItem = createAsyncThunk<
  CatalogItem,
  CreateCatalogItemProps,
  CreateAsyncThunk
>(
  'catalog/createCatalogItem',
  async (
    { assistantId, catalogItem, catalogItemDetails },
    { getState, rejectWithValue, dispatch }
  ) => {
    try {
      const safeAssistantId = assistantId ?? getAssistantId(getState());
      let safeCatalogItem = catalogItem;

      if (!_.isNil(catalogItemDetails)) {
        const { title, description, specification } = catalogItemDetails;

        safeCatalogItem = {
          title,
          description,
          specification
        };
      }

      // createdObjectEvents
      const response = await api.createCatalogIntegrationInstance(
        safeAssistantId,
        safeCatalogItem
      );

      return response;
    } catch (error) {
      if (
        error.response &&
        (error.response.status === 403 || error.response.status === 401)
      ) {
        dispatch(addErrorMessage(error));
      }
      return rejectWithValue(error);
    }
  }
);

// Array of catalog item titles that are NOT supported on CPD.
const UnsupportedCatalogItems = Object.freeze(['Segment']);

type HasTitle = {
  title: string;
};

/**
 * Filters the provided list of catalog items and if we are in CPD, excludes
 * the ones that are not supported in CPD
 *
 * @param catalogItems The array of catalog items to filter by title
 * @returns
 */
const filterUnsupportedCatalogItems = <T extends HasTitle>(
  catalogItems: Array<T>
): Array<T> =>
  isCpd
    ? catalogItems.filter(
        catalogItem => !UnsupportedCatalogItems.includes(catalogItem.title)
      )
    : catalogItems;

export const createDefaultCatalogItems = createAsyncThunk<
  void,
  {
    assistantId: string;
    currentItems: CatalogItem[];
    catalogItemsInState: CatalogItem[];
  },
  CreateAsyncThunk
>(
  'catalog/createDefaultCatalogItems',
  async (
    { assistantId, currentItems, catalogItemsInState },
    { dispatch, getState }
  ) => {
    const state = getState();
    const currentAssistantId = getAssistantId(state);
    const isWriter = isWriterOfAssistant(state, assistantId);

    // Only add catalog items if it is the same assistant.
    // Reader roles cannot add to catalog.
    if (currentAssistantId !== assistantId || !isWriter) {
      return;
    }

    // Using a Set to track unique catalog item titles from both state and current call
    // to avoid creation of multiple default segments due to rerenders
    const existingCatalogItems = new Set([
      ...currentItems.map(item => item.title),
      ...catalogItemsInState.map(item => item.title)
    ]);
    // Some catalog items are considered "default" as they must be
    // created before Tanya sees or interacts with them. As an
    // example, we create the Segment catalog item before the
    // Integrations page lists all the catalog items onto the page.
    // We must furthermore filter any ones that are not supported when
    // we are in CPD
    const defaultCatalogItems = filterUnsupportedCatalogItems(
      getDefaultCatalogItems() as CatalogItemDetails[]
    );

    for (const item of defaultCatalogItems) {
      // Only create default items if its not already present
      if (!existingCatalogItems.has(item.title)) {
        try {
          const createdItem = await dispatch(
            createCatalogItem({
              assistantId,
              catalogItemDetails: item
            })
          ).unwrap();

          // Add the title of the newly created item to the Set
          existingCatalogItems.add(createdItem.title);
        } catch (error) {
          console.error('Error creating catalog item:', item, error);
        }
      }
    }
  }
);

export const listCatalogItems = createAsyncThunk<
  CatalogItem[],
  string,
  CreateAsyncThunk
>('catalog/listCatalogItems', async (assistantId, { dispatch, getState }) => {
  try {
    const state = getState();
    const isNewIA = selectIsNewIa(state);

    if (!isNewIA) {
      return [];
    }

    const response = await api.listCatalogIntegrations(assistantId);

    return filterUnsupportedCatalogItems(
      response.catalog_items as CatalogItem[]
    );
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

interface LoadCatalogItemProps {
  assistantId: string;
  catalogItemId: string;
}
/**
 *
 *
 */
export const loadCatalogItem = createAsyncThunk<
  CatalogItem,
  LoadCatalogItemProps,
  CreateAsyncThunk
>(
  'catalog/loadCatalogItem',
  async ({ assistantId, catalogItemId }, { dispatch }) => {
    try {
      return api.getCatalogItem(assistantId, catalogItemId);
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

interface DeleteCatalogItemProps {
  assistantId: string;
  catalogItemId?: string;
  catalogItem?: CatalogItem;
}
/**
 *
 */
export const deleteCatalogItem = createAsyncThunk<
  string,
  DeleteCatalogItemProps,
  CreateAsyncThunk
>(
  'catalog/deleteCatalogItem',
  async ({ assistantId, catalogItemId, catalogItem }, { dispatch }) => {
    try {
      const safeCatalogItemId = catalogItemId ?? catalogItem.catalog_item_id;
      await api.deleteCatalogItem(assistantId, safeCatalogItemId);
      return safeCatalogItemId;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  }
);

export const resetCurrentCatalogItem = createAction(
  'catalog/resetCurrentCatalogItem'
);

export const resetAllCatalogItems = createAction(
  'catalog/resetAllCatalogItems'
);
